/**
 * BlockLoader - Async wrapper for individual block loading
 *
 * Each block fetches its own data independently, so:
 * - A failing block doesn't break other blocks
 * - Blocks can load at different speeds (progressive loading)
 * - Better perceived performance
 *
 * Features:
 * - Collapsible blocks: Users can minimize blocks to save space
 * - Remind me later: Temporarily hide blocks
 * - Permanent dismiss: Hide blocks forever (with confirmation)
 * - State persisted to localStorage (anonymous) or DB (authenticated)
 *
 * Props:
 * - blockId: Block identifier
 * - clientId: Client identifier
 * - authSessionId: Session ID for authenticated API requests
 * - memberId: Member ID for localStorage caching (separate cache per user)
 * - onError: Callback for block errors
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fetchBlockData, blockAction } from '../lib/api';
import { BLOCKS, getBlockPreview, getBlockRequestParams } from './index';

// LocalStorage key prefix for block state
const BLOCK_STATE_PREFIX = 'block_state_';

// Duration display text
const DURATION_TEXT = {
  '1_day': '1 day',
  '3_days': '3 days',
  '1_week': '1 week',
  next_session: 'this session',
};

// Convert hex to RGB for alpha manipulation
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 99, g: 102, b: 241 }; // fallback to indigo
}

function getStateKey(clientId, blockId, memberId) {
  if (memberId) {
    return `${BLOCK_STATE_PREFIX}${clientId}_${blockId}_${memberId}`;
  }
  return `${BLOCK_STATE_PREFIX}${clientId}_${blockId}`;
}

function getBlockState(storageKey) {
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return { state: 'visible' };
    return JSON.parse(stored);
  } catch {
    return { state: 'visible' };
  }
}

function saveBlockState(storageKey, stateData) {
  try {
    localStorage.setItem(
      storageKey,
      JSON.stringify({
        ...stateData,
        updated_at: new Date().toISOString(),
      }),
    );
  } catch {
    // Ignore localStorage errors
  }
}

function isRemindExpired(remindUntil) {
  if (!remindUntil) return true;
  return new Date(remindUntil) <= new Date();
}

function getBlockDisplayName(blockId) {
  const names = {
    'quick-poll': 'Quick Poll',
    'smart-block': 'Quick Question',
  };
  return (
    names[blockId] ||
    blockId
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  );
}

export default function BlockLoader({
  blockId,
  clientId,
  authSessionId,
  memberId,
  primaryColor,
  onError,
}) {
  const [data, setData] = useState(null);
  const [blockConfig, setBlockConfig] = useState({});
  const [blockState, setBlockState] = useState('visible');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDismissMenu, setShowDismissMenu] = useState(false);
  const [actionPending, setActionPending] = useState(false);
  const menuRef = useRef(null);

  const storageKey = useMemo(
    () => getStateKey(clientId, blockId, memberId),
    [clientId, blockId, memberId],
  );

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowDismissMenu(false);
      }
    }

    if (showDismissMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDismissMenu]);

  // Check local state on mount
  useEffect(() => {
    const stored = getBlockState(storageKey);

    if (stored.state === 'dismissed') {
      setBlockState('dismissed');
      setLoading(false);
      return;
    }

    if (stored.state === 'remind_later') {
      if (isRemindExpired(stored.remind_until)) {
        // Reminder expired, reset to visible
        saveBlockState(storageKey, { state: 'visible' });
        setBlockState('visible');
      } else {
        setBlockState('remind_later');
        setLoading(false);
        return;
      }
    }

    if (stored.state === 'collapsed') {
      setIsCollapsed(true);
    }
  }, [storageKey]);

  const loadBlockData = useCallback(async () => {
    if (!clientId || !blockId) return;

    const stored = getBlockState(storageKey);
    if (stored.state === 'dismissed') {
      setLoading(false);
      return;
    }

    if (stored.state === 'remind_later' && !isRemindExpired(stored.remind_until)) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const options = authSessionId ? { authSessionId } : {};
      const queryParams = getBlockRequestParams(blockId, { clientId, memberId });
      if (queryParams) {
        options.queryParams = queryParams;
      }
      const result = await fetchBlockData(clientId, blockId, options);

      setData(result.data);
      setBlockConfig(result.config || {});

      // Apply default collapsed state if not already set locally
      const currentState = getBlockState(storageKey);
      if (result.config?.default_collapsed && currentState.state === 'visible') {
        setIsCollapsed(true);
      }
    } catch (err) {
      console.error(`Block ${blockId} failed to load:`, err);
      setError(err.message);
      onError?.(blockId, err);
    } finally {
      setLoading(false);
    }
  }, [clientId, blockId, authSessionId, memberId, onError, storageKey]);

  useEffect(() => {
    loadBlockData();
  }, [loadBlockData]);

  // Handle collapse/expand
  const handleToggleCollapse = useCallback(async () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);

    const newState = newCollapsed ? 'collapsed' : 'visible';
    saveBlockState(storageKey, { state: newState });

    try {
      const options = authSessionId ? { authSessionId } : {};
      await blockAction(
        blockId,
        {
          client_id: clientId,
          action: newCollapsed ? 'collapse' : 'expand',
        },
        options,
      );
    } catch (err) {
      console.error(`Failed to ${newCollapsed ? 'collapse' : 'expand'} block:`, err);
    }
  }, [isCollapsed, blockId, clientId, authSessionId, storageKey]);

  // Handle remind later
  const handleRemindLater = useCallback(async () => {
    setActionPending(true);
    setShowDismissMenu(false);

    const duration = blockConfig.remind_later_duration || '1_day';

    try {
      const options = authSessionId ? { authSessionId } : {};
      const result = await blockAction(
        blockId,
        {
          client_id: clientId,
          action: 'remind_later',
          duration,
        },
        options,
      );

      saveBlockState(storageKey, {
        state: 'remind_later',
        remind_until: result.remind_until,
      });

      setBlockState('remind_later');
    } catch (err) {
      console.error('Failed to set remind later:', err);
    } finally {
      setActionPending(false);
    }
  }, [blockId, clientId, authSessionId, storageKey, blockConfig]);

  // Handle permanent dismiss
  const handleDismiss = useCallback(async () => {
    // Show confirmation
    const confirmed = window.confirm(
      "Are you sure you want to permanently hide this block? You won't see it again.",
    );

    if (!confirmed) {
      setShowDismissMenu(false);
      return;
    }

    setActionPending(true);
    setShowDismissMenu(false);

    try {
      const options = authSessionId ? { authSessionId } : {};
      await blockAction(
        blockId,
        {
          client_id: clientId,
          action: 'dismiss',
          confirmed: true,
        },
        options,
      );

      saveBlockState(storageKey, { state: 'dismissed' });
      setBlockState('dismissed');
    } catch (err) {
      console.error('Failed to dismiss block:', err);
      // Still dismiss locally even if API fails
      saveBlockState(storageKey, { state: 'dismissed' });
      setBlockState('dismissed');
    } finally {
      setActionPending(false);
    }
  }, [blockId, clientId, authSessionId, storageKey]);

  // Get the component for this block
  const Component = BLOCKS[blockId];

  if (!Component) {
    console.warn(`No component registered for block: ${blockId}`);
    return null;
  }

  // Don't render if dismissed or remind_later active
  if (blockState === 'dismissed' || blockState === 'remind_later') {
    return null;
  }

  if (loading) return null;
  if (error) return null;
  if (!data) return null;

  const showCollapseButton = blockConfig.allow_collapse !== false;
  const showDismissOptions = blockConfig.allow_remind_later || blockConfig.allow_permanent_dismiss;

  return (
    <div className="relative group">
      {/* Block Header with controls (visible on hover, hidden when collapsed) */}
      {(showCollapseButton || showDismissOptions) && !isCollapsed && (
        <div
          className="absolute top-2 right-2 z-10 flex items-center gap-1
          opacity-0 group-hover:opacity-100 transition-opacity duration-150"
        >
          {/* Collapse/Expand button */}
          {showCollapseButton && (
            <button
              onClick={handleToggleCollapse}
              className="p-1.5 rounded-full text-slate-400 hover:text-slate-600
                hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300"
              aria-label={isCollapsed ? 'Expand block' : 'Collapse block'}
              title={isCollapsed ? 'Expand' : 'Minimize'}
            >
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                {isCollapsed ? (
                  <polyline points="6 9 12 15 18 9" />
                ) : (
                  <polyline points="18 15 12 9 6 15" />
                )}
              </svg>
            </button>
          )}

          {/* Dismiss menu trigger */}
          {showDismissOptions && (
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowDismissMenu(!showDismissMenu)}
                disabled={actionPending}
                className="p-1.5 rounded-full text-slate-400 hover:text-slate-600
                  hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300"
                aria-label="Dismiss options"
              >
                {actionPending ? (
                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="5" r="2" />
                    <circle cx="12" cy="12" r="2" />
                    <circle cx="12" cy="19" r="2" />
                  </svg>
                )}
              </button>

              {/* Dismiss dropdown menu */}
              {showDismissMenu && (
                <div
                  className="absolute right-0 top-full mt-1 w-52 bg-white rounded-lg shadow-lg
                  border border-slate-200 py-1 z-20"
                >
                  {blockConfig.allow_remind_later && (
                    <button
                      onClick={handleRemindLater}
                      className="w-full px-3 py-2 text-left text-sm text-slate-700
                        hover:bg-slate-50 flex items-center gap-2"
                    >
                      <svg
                        className="w-4 h-4 text-slate-400"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      <span>Remind me later</span>
                      <span className="text-xs text-slate-400 ml-auto">
                        {DURATION_TEXT[blockConfig.remind_later_duration] || '1 day'}
                      </span>
                    </button>
                  )}

                  {blockConfig.allow_permanent_dismiss && (
                    <button
                      onClick={handleDismiss}
                      className="w-full px-3 py-2 text-left text-sm text-slate-700
                        hover:bg-slate-50 flex items-center gap-2"
                    >
                      <svg
                        className="w-4 h-4 text-slate-400"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                      <span>Don't show again</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Collapsed state - minimal header, with accent gradient for blocks that have accent_color */}
      {isCollapsed ? (
        (() => {
          // Only apply gradient if block has an accent_color configured
          const accentColor = blockConfig?.accent_color;

          let bgStyle = {};
          let titleStyle = {};

          if (accentColor) {
            const rgb = hexToRgb(accentColor);
            bgStyle = {
              background: `linear-gradient(to right, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.08), white 40%)`,
            };
            titleStyle = { color: accentColor };
          }

          return (
            <div
              onClick={handleToggleCollapse}
              className={`rounded-xl border border-slate-200 p-3 cursor-pointer
                hover:border-slate-300 transition-colors overflow-hidden ${!accentColor ? 'bg-white' : ''}`}
              style={bgStyle}
            >
              <div className="flex items-center justify-between gap-4">
                <span
                  className={`text-sm font-medium truncate ${!accentColor ? 'text-slate-600' : ''}`}
                  style={titleStyle}
                >
                  {getBlockDisplayName(blockId)}
                  {(() => {
                    const preview = getBlockPreview(blockId, data);
                    return preview ? (
                      <span className="font-normal text-slate-500">: {preview}</span>
                    ) : null;
                  })()}
                </span>
                <span className="flex items-center gap-2 text-xs text-slate-400 shrink-0">
                  Click to expand
                  <svg
                    className="w-3 h-3"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </span>
              </div>
            </div>
          );
        })()
      ) : (
        /* Full block content */
        <Component
          data={data}
          config={blockConfig}
          onRefresh={loadBlockData}
          clientId={clientId}
          authSessionId={authSessionId}
          memberId={memberId}
          primaryColor={primaryColor}
        />
      )}
    </div>
  );
}
